import React from "react";
import {
  Typography,
  FormControl,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableHead,
  Box,
  Grid,
  FormHelperText,
  CircularProgress,
  LinearProgress,
  IconButton,
  Autocomplete,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { useFormik } from "formik";
import * as yup from "yup";
import { useParams } from "react-router-dom";
import { useGetTeachersPaginatedQuery } from "../staff/staffApiSlice";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { useGetSubjectsPaginatedQuery } from "../subjects/subjectsApiSlice";
import {
  useAddSubjectToClassroomMutation,
  useGetClassroomWithSubjectsQuery,
  useRemoveSubjectFromClassroomMutation,
  useReplaceSubjectTeacherMutation,
} from "../classroom/classApiSlice";
import toast from "../../../helpers/toast";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";

function ClassSubjects() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  let { classId } = useParams();
  const [openWarningDialog, setOpenWarningDialog] = React.useState(false);
  const [focusedSubject, setFocusedSubject] = React.useState(null);
  const [editingSubjectId, setEditingSubjectId] = React.useState(null);
  const [newTeacherId, setNewTeacherId] = React.useState("");
  const [teacherSearch, setTeacherSearch] = React.useState("");
  const [editTeacherSearch, setEditTeacherSearch] = React.useState("");
  const { data: teachersAddData } = useGetTeachersPaginatedQuery({ page: 1, per_page: 10, q: teacherSearch });
  const teacherOptions = teachersAddData?.teachers ?? [];
  const { data: teachersEditData } = useGetTeachersPaginatedQuery({ page: 1, per_page: 10, q: editTeacherSearch });
  const teacherEditOptions = teachersEditData?.teachers ?? [];
  const [subjectSearch, setSubjectSearch] = React.useState("");
  const { data: subjectsData, isLoading: getSubjectLoading } = useGetSubjectsPaginatedQuery({ page: 1, per_page: 10, q: subjectSearch });
  const subjectOptions = subjectsData?.subjects ?? [];
  const { data: classroom } = useGetClassroomWithSubjectsQuery(classId);

  const [addSubjectToClass, { isLoading: addSubjectToClassLoading }] =
    useAddSubjectToClassroomMutation();

  const [
    removeSubjectFromClassroom,
    { isLoading: removeSubjectFromClassroomLoading },
  ] = useRemoveSubjectFromClassroomMutation();

  const [replaceSubjectTeacher, { isLoading: replaceSubjectTeacherLoading }] =
    useReplaceSubjectTeacherMutation();

  const getTeacherName = (teacherId) => {
    if (!teacherId) return "-";
    const fromEdit = teacherEditOptions.find((t) => t.user_id === teacherId);
    if (fromEdit) return `${fromEdit.first_name} ${fromEdit.last_name}`;
    const fromAdd = teacherOptions.find((t) => t.user_id === teacherId);
    if (fromAdd) return `${fromAdd.first_name} ${fromAdd.last_name}`;
    return "-";
  };

  const handleStartEditTeacher = (row) => {
    setEditingSubjectId(row.id);
    setNewTeacherId(row.teacher_id || "");
    setEditTeacherSearch("");
  };

  const handleCancelEditTeacher = () => {
    setEditingSubjectId(null);
    setNewTeacherId("");
    setEditTeacherSearch("");
  };

  const handleSaveTeacher = async (subjectId) => {
    try {
      const response = await replaceSubjectTeacher({
        class_Id: classId,
        subject_id: subjectId,
        teacher_id: newTeacherId,
      }).unwrap();
      if (response.message) {
        toast.success("Teacher updated successfully");
        setEditingSubjectId(null);
        setNewTeacherId("");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const formik = useFormik({
    initialValues: {
      teacher: "",
      subject: "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      teacher: yup.string().required("Required"),
      subject: yup.string().required("Required"),
    }),
    onSubmit: async (values) => {
      const body = {
        class_Id: classId,
        teacher_id: values.teacher,
        subject_id: values.subject,
      };
      try {
        const response = await addSubjectToClass(body).unwrap();
        if (response.message) {
          formik.resetForm();
          toast.success("Subject added successfully");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  const handleRemoveSubject = async () => {
    const body = {
      class_Id: classId,
      subject_Id: focusedSubject.id,
    };
    try {
      const response = await removeSubjectFromClassroom(body).unwrap();
      if (response.message) {
        toast.success(response.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <div style={{ padding: "0 5px" }}>
      {/* modals */}
      <CustomWarningDialog
        open={openWarningDialog}
        setOpen={setOpenWarningDialog}
        item={focusedSubject}
        action={handleRemoveSubject}
        message="!!!WARNING!!! This will remove the subject from the class. Are you sure you want to continue?"
      />
      <Grid container columnSpacing={3}>
        <Grid item xs={12} md={6}>
          <StyledPaper sx={{ padding: "15px" }}>
            <Typography variant="formHeading" sx={{ marginBottom: "20px" }}>
              New Class Subject Form
            </Typography>

            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl
                    error={formik.touched.subject && formik.errors.subject}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">SUBJECT</Typography>
                    <Autocomplete
                      options={subjectOptions}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={subjectOptions.find((s) => s.id === formik.values.subject) || null}
                      onChange={(_, value) => formik.setFieldValue("subject", value?.id || "")}
                      onInputChange={(_, value, reason) => { if (reason === "input") setSubjectSearch(value); }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search subject..."
                          error={formik.touched.subject && Boolean(formik.errors.subject)}
                        />
                      )}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.subject && formik.errors.subject}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl
                    error={formik.touched.teacher && formik.errors.teacher}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">TEACHER</Typography>
                    <Autocomplete
                      options={teacherOptions}
                      getOptionLabel={(option) => option ? `${option.first_name} ${option.last_name}` : ""}
                      isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                      value={teacherOptions.find((t) => t.user_id === formik.values.teacher) || null}
                      onChange={(_, value) => formik.setFieldValue("teacher", value?.user_id || "")}
                      onInputChange={(_, value, reason) => { if (reason === "input") setTeacherSearch(value); }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search teacher..."
                          error={formik.touched.teacher && Boolean(formik.errors.teacher)}
                        />
                      )}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.teacher && formik.errors.teacher}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12} textAlign={"right"}>
                  <StyledLoadingButton
                    loading={addSubjectToClassLoading}
                    type="submit"
                  >
                    Add
                  </StyledLoadingButton>
                </Grid>
              </Grid>
            </Box>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledPaper>
            <Box sx={{ padding: "15px" }}>
              <Typography variant="formHeading">
                Current Subjects in Class
              </Typography>
              <Typography variant="formSubHeading"></Typography>
            </Box>
            {getSubjectLoading && <LinearProgress />}
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                {classroom?.subjects?.map((row) => (
                  <Box
                    key={row.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ color: "primary.main", fontWeight: 600 }}>
                          {row.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {row.description || "-"}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Teacher
                        </Typography>
                        {editingSubjectId === row.id ? (
                          <Box>
                            <Autocomplete
                              options={teacherEditOptions}
                              getOptionLabel={(option) => option ? `${option.first_name} ${option.last_name}` : ""}
                              isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                              value={teacherEditOptions.find((t) => t.user_id === newTeacherId) || null}
                              onChange={(_, value) => setNewTeacherId(value?.user_id || "")}
                              onInputChange={(_, value, reason) => { if (reason === "input") setEditTeacherSearch(value); }}
                              filterOptions={(x) => x}
                              renderInput={(params) => (
                                <StyledTextField
                                  {...params}
                                  placeholder="Search teacher..."
                                  size="small"
                                />
                              )}
                            />
                            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                              {replaceSubjectTeacherLoading && editingSubjectId === row.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <>
                                  <IconButton size="small" onClick={() => handleSaveTeacher(row.id)} disabled={!newTeacherId}>
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={handleCancelEditTeacher}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Stack>
                          </Box>
                        ) : (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2">{row.teacher_name || "-"}</Typography>
                            <IconButton size="small" onClick={() => handleStartEditTeacher(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        )}
                      </Box>

                      <Box>
                        {removeSubjectFromClassroomLoading && focusedSubject === row ? (
                          <CircularProgress size={20} />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setFocusedSubject(row);
                              setOpenWarningDialog(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "2px solid lightgrey " }}>
                      <StyledTableCell>SUBJECT</StyledTableCell>
                      <StyledTableCell>Description</StyledTableCell>
                      <StyledTableCell>TEACHER</StyledTableCell>
                      <StyledTableCell align={"right"}>DELETE</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classroom?.subjects?.map((row) => (
                      <StyledTableRow key={row.id}>
                        <StyledTableCell component="th" scope="row">
                          {row.name}
                        </StyledTableCell>
                        <StyledTableCell component="th" scope="row">
                          {row.description}
                        </StyledTableCell>
                        <StyledTableCell component="th" scope="row">
                          {editingSubjectId === row.id ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Autocomplete
                                options={teacherEditOptions}
                                getOptionLabel={(option) => option ? `${option.first_name} ${option.last_name}` : ""}
                                isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                                value={teacherEditOptions.find((t) => t.user_id === newTeacherId) || null}
                                onChange={(_, value) => setNewTeacherId(value?.user_id || "")}
                                onInputChange={(_, value, reason) => { if (reason === "input") setEditTeacherSearch(value); }}
                                filterOptions={(x) => x}
                                sx={{ minWidth: 180 }}
                                renderInput={(params) => (
                                  <StyledTextField
                                    {...params}
                                    placeholder="Search teacher..."
                                    size="small"
                                  />
                                )}
                              />
                              {replaceSubjectTeacherLoading && editingSubjectId === row.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <>
                                  <IconButton size="small" onClick={() => handleSaveTeacher(row.id)} disabled={!newTeacherId}>
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={handleCancelEditTeacher}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {row.teacher_name || "-"}
                              <IconButton size="small" onClick={() => handleStartEditTeacher(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          {removeSubjectFromClassroomLoading &&
                          focusedSubject === row ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon
                              className="tableCellLink"
                              onClick={() => {
                                setFocusedSubject(row);
                                setOpenWarningDialog(true);
                              }}
                            />
                          )}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </StyledPaper>
        </Grid>
      </Grid>
    </div>
  );
}

export default ClassSubjects;
